import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { mdiPlus } from '@mdi/js';
import { useNavigate } from 'react-router';

import { IFeature } from 'graphql/types';
import { useFeature } from 'common/hooks/useFeature';
import { ActionButton } from 'common/components/ActionButton';
import { SkillsManagementTable } from 'skill/components/SkillsManagementTable';
import { PageTitle } from 'common/components/PageTitle';
import { Icon } from 'common/components/Icon';
import { FourOFourPage } from 'hydra/pages/FourOFourPage';
import { UpgradeContent } from 'skill/components/UpgradeContent';
import { useAppPermissions } from 'user/hooks/usePermissions';
import { IGlobalDrawerType } from 'common/types';
import { useGlobalDrawer } from 'common/hooks/useGlobalDrawer';
import { getUrl } from 'route/utils/getUrl';
import { Alert } from 'common/components/Alert';
import { Link } from 'common/components/Link';

import { BasePage } from './BasePage';

export const SkillsManagementPage = () => {
    const [translate] = useTranslation();
    const navigate = useNavigate();

    const {
        canUse: canUseSkill,
        showSkills,
        userCanSeeUpgrade,
        loading: loadingFeature,
    } = useFeature(IFeature.Skill, true);
    const { canRead = false } = useAppPermissions('skill', 'management');
    const { openGlobalDrawer } = useGlobalDrawer();

    const showUpgradePage = !canUseSkill && userCanSeeUpgrade;

    // User cannot use skill module and cannot see upgrade page
    if (
        (!loadingFeature && !canUseSkill && !userCanSeeUpgrade) ||
        (!canRead && !userCanSeeUpgrade)
    ) {
        return <FourOFourPage />;
    }

    const handleCreateSkill = () => {
        // Navigate to the skill management list page with the current search
        // params after the skill is created
        navigate(getUrl('SKILL_MANAGEMENT_LIST'));
    };

    return (
        <BasePage contentContainerMaxWidth="lg">
            <PageTitle>
                {translate('skillsManagementPage.browserTitle')}
            </PageTitle>

            {!showUpgradePage ? (
                <>
                    <Box sx={{ pt: 4 }}>
                        <Grid
                            container
                            spacing={2}
                            sx={{ alignItems: 'center' }}
                        >
                            <Grid size="grow">
                                <Typography variant="h1">
                                    {translate(
                                        'skillsManagementPage.pageTitle'
                                    )}
                                </Typography>
                            </Grid>

                            <Grid size="auto">
                                <ActionButton
                                    outlined
                                    variant="extended"
                                    onClick={() => {
                                        openGlobalDrawer({
                                            type: IGlobalDrawerType.SkillCreate,
                                            props: {
                                                onCreate: handleCreateSkill,
                                            },
                                        });
                                    }}
                                >
                                    <Box>
                                        <Icon path={mdiPlus} size="2.4rem" />
                                    </Box>

                                    {translate('skillsManagementPage.newSkill')}
                                </ActionButton>
                            </Grid>
                        </Grid>

                        {!showSkills && (
                            <Box sx={{ mt: 2, mb: -2 }}>
                                <Alert
                                    action={
                                        <Button
                                            color="inherit"
                                            component={Link}
                                            size="small"
                                            sx={{ fontWeight: 'bold', mr: 1 }}
                                            to="ORGANISATION_SETTINGS"
                                        >
                                            {translate('settings')}
                                        </Button>
                                    }
                                    severity="error"
                                >
                                    {translate(
                                        'skillsManagementPage.notPublishedMessage'
                                    )}
                                </Alert>
                            </Box>
                        )}
                    </Box>

                    {/* Prevent unnecessary query by the table when user can not manage */}
                    <Box sx={{ py: 4 }}>
                        <SkillsManagementTable />
                    </Box>
                </>
            ) : (
                <UpgradeContent />
            )}
        </BasePage>
    );
};
